import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { deleteProduct, getProducts } from "../products.action";
import { resetDeleteProduct } from "../products.slice";
import { toast } from "sonner";

interface IDeleteProductInterface {
  id: string;
  isDeleteOpen: boolean;
  setIsDeleteOpen: (open: boolean) => void;
}

export default function DeleteProduct({
  id,
  isDeleteOpen,
  setIsDeleteOpen,
}: IDeleteProductInterface) {
  // Hooks
  const dispatch = useAppDispatch();

  // Redux
  const { deleteProductError, deleteProductSuccess, filters } = useAppSelector(
    (state) => state.products
  );

  // States
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    dispatch(deleteProduct(id));
  };

  useEffect(() => {
    if (!isDeleting) return;

    if (deleteProductSuccess) {
      setIsDeleteOpen(false);
      setIsDeleting(false);
      dispatch(getProducts(filters));
      toast.success("Product deleted successfully");
    }

    if (deleteProductError) {
      setIsDeleting(false);
      toast.error(deleteProductError);
    }

    return () => {
      dispatch(resetDeleteProduct());
    };
  }, [deleteProductSuccess, deleteProductError]);

  return (
    <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the
            product and remove it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setIsDeleteOpen(false)}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handleDelete()}
            disabled={isDeleting}
          >
            Continue
            {isDeleting && <Loader2 className="animate-spin" />}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
